import { JitsiMeeting } from '@jitsi/react-sdk'
import { JITSI_DOMAIN } from "../../constants/urls"
import { useRef, useEffect, useMemo, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { Box } from '@mui/material'
import { setUiDisabled } from '../../store/slices/appSlice'
import { appColors } from '../../constants/theme'
import { readMeetingPermissionConsent } from '../../utils/meetingMediaPermission'
import MeetingMediaControls, { MeetingLobbyPreview } from './MeetingMediaControls'
import MeetingDevicePermissionDialog from './MeetingDevicePermissionDialog'

/** Görüşmede Jitsi araç çubuğu — hangup / ekran paylaşımı / el kaldırma yok */
const TOOLBAR_BUTTONS = [
  'microphone',
  'camera',
  'fullscreen',
  'settings',
  'tileview',
]

const Jitsi = ({ roomName, displayName, jwtToken, onUserReady, onConferenceLeft, userId }) => {
  const jitsiApiRef = useRef(null);
  const lockSessionIdRef = useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const lockLostRef = useRef(false);
  const isUnmountingRef = useRef(false);
  const hasJoinedRef = useRef(false);
  const leftNotifiedRef = useRef(false);
  /** false = bizim lobi; true = Jitsi mount (prejoin kapalı, otomatik odaya giriş) */
  const [hasStartedMeeting, setHasStartedMeeting] = useState(false);
  const [isConferenceJoined, setIsConferenceJoined] = useState(false);
  const [isApiReady, setIsApiReady] = useState(false);
  /** Lobide/görüşmede consent yokken açılan cihaz izin modalı: 'audio' | 'video' | null */
  const [permissionPromptKind, setPermissionPromptKind] = useState(null);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const JOIN_LOCK_TTL_MS = 10000;

  const initialConsent = useMemo(
    () => readMeetingPermissionConsent(roomName),
    [roomName]
  );

  const [audioMuted, setAudioMuted] = useState(
    () => !initialConsent.microphonePermissionConsent
  );
  const [videoMuted, setVideoMuted] = useState(
    () => !initialConsent.cameraPermissionConsent
  );
  /** Katıl anındaki mute tercihleri — görüşme sırasında config değişmesin / remount olmasın */
  const [joinMediaPrefs, setJoinMediaPrefs] = useState({
    audioMuted: !initialConsent.microphonePermissionConsent,
    videoMuted: !initialConsent.cameraPermissionConsent,
  });

  const joinLockKey = useMemo(() => {
    if (!roomName || !userId) return null;
    return `jitsi:join-lock:${roomName}:${userId}`;
  }, [roomName, userId]);

  const readJoinLock = useCallback(() => {
    if (!joinLockKey || typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(joinLockKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.sessionId || typeof parsed.updatedAt !== 'number') return null;
      return parsed;
    } catch {
      return null;
    }
  }, [joinLockKey]);

  const writeJoinLock = useCallback(() => {
    if (!joinLockKey || typeof window === 'undefined') return;
    window.localStorage.setItem(
      joinLockKey,
      JSON.stringify({
        sessionId: lockSessionIdRef.current,
        updatedAt: Date.now(),
      })
    );
  }, [joinLockKey]);

  const removeJoinLockIfOwned = useCallback(() => {
    if (!joinLockKey || typeof window === 'undefined') return;
    const lock = readJoinLock();
    if (lock?.sessionId === lockSessionIdRef.current) {
      window.localStorage.removeItem(joinLockKey);
    }
  }, [joinLockKey, readJoinLock]);

  const handleJoinLockLost = useCallback(() => {
    if (lockLostRef.current) return;
    lockLostRef.current = true;
    try {
      jitsiApiRef.current?.executeCommand?.('hangup');
    } catch {
      // no-op
    }
  }, []);

  const configOverwrite = useMemo(() => ({
    /** Medilya lobisi kullanılıyor — Jitsi ön katılım ekranı kapalı */
    prejoinConfig: {
      enabled: false,
      hideDisplayName: true,
    },
    prejoinPageEnabled: false,
    disablePrejoinPage: true,
    enableWelcomePage: false,
    welcomePage: { disabled: true },
    disableDeepLinking: true,
    disableInviteFunctions: true,
    defaultLanguage: "tr",
    disableInitialGUM: false,
    startSilent: false,
    startWithAudioMuted: joinMediaPrefs.audioMuted,
    startWithVideoMuted: joinMediaPrefs.videoMuted,
    readOnlyName: true,
    hideConferenceSubject: true,
    /** Kendi görüntü: speaker görünümünde ayrı (PiP / filmstrip) panel */
    disableSelfView: false,
    disableSelfViewSettings: false,
    enableLayerSuspension: false,
    showDisplayNameOnVideo: true,
    showParticipantNameOnVideo: true,
    filmstrip: {
      disableFilmstripAutohiding: true,
    },
    toolbarButtons: TOOLBAR_BUTTONS,
    toolbarConfig: {
      alwaysVisible: true,
    },
  }), [joinMediaPrefs.audioMuted, joinMediaPrefs.videoMuted]);

  const interfaceConfigOverwrite = useMemo(() => ({
    SHOW_BRAND_WATERMARK: false,
    BRAND_WATERMARK_LINK: "",
    DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
    SHOW_JITSI_WATERMARK: false,
    SHOW_WATERMARK_FOR_GUESTS: false,
    JITSI_WATERMARK_LINK: "",
    SHOW_POWERED_BY: false,
    APP_NAME: "Medilya",
    DEFAULT_LANGUAGE: "tr",
    VERTICAL_FILMSTRIP: true,
    SETTINGS_SECTIONS: ['devices', 'language', 'moderator', 'calendar', 'sounds', 'more'],
    TOOLBAR_BUTTONS: TOOLBAR_BUTTONS,
  }), []);

  useEffect(() => {
    dispatch(setUiDisabled(true));
  }, [dispatch]);

  useEffect(() => {
    isUnmountingRef.current = false;
    return () => {
      isUnmountingRef.current = true;
      hasJoinedRef.current = false;
      setIsConferenceJoined(false);
      setIsApiReady(false);
      removeJoinLockIfOwned();
      try {
        jitsiApiRef.current?.dispose?.();
      } catch {
        /* iframe zaten kalkmış olabilir */
      }
      jitsiApiRef.current = null;
    };
  }, [removeJoinLockIfOwned]);

  const syncMuteStateFromApi = useCallback(async () => {
    const api = jitsiApiRef.current;
    if (!api) return;
    try {
      const [aMuted, vMuted] = await Promise.all([
        api.isAudioMuted?.() ?? true,
        api.isVideoMuted?.() ?? true,
      ]);
      setAudioMuted(!!aMuted);
      setVideoMuted(!!vMuted);
    } catch {
      /* ignore */
    }
  }, []);

  const resetToLobby = useCallback(() => {
    try {
      jitsiApiRef.current?.dispose?.();
    } catch {
      /* ignore */
    }
    jitsiApiRef.current = null;
    hasJoinedRef.current = false;
    setIsConferenceJoined(false);
    setIsApiReady(false);
    setHasStartedMeeting(false);
  }, []);

  const handleVideoConferenceLeft = useCallback(() => {
    hasJoinedRef.current = false;
    setIsConferenceJoined(false);
    removeJoinLockIfOwned();
    console.log('Jitsi meeting videoConferenceLeft');
    if (isUnmountingRef.current) return;
    if (leftNotifiedRef.current) return;
    leftNotifiedRef.current = true;
    resetToLobby();
    if (typeof onConferenceLeft === 'function') {
      onConferenceLeft();
    }
  }, [onConferenceLeft, removeJoinLockIfOwned, resetToLobby]);

  const handleVideoconferenceLeft = handleVideoConferenceLeft;

  /** Prejoin sonrası sadece senkronize et — parent’tan otomatik unmute jest taşımadığı için GUM’u bozuyor */
  const handleVideoConferenceJoined = useCallback(() => {
    if (hasJoinedRef.current || isUnmountingRef.current) return;
    hasJoinedRef.current = true;
    leftNotifiedRef.current = false;
    lockLostRef.current = false;
    writeJoinLock();
    setIsConferenceJoined(true);
    try {
      // Tile view yerine speaker view: karşı taraf büyük, kendi görüntü ayrı (self-view)
      jitsiApiRef.current?.executeCommand?.("setTileView", false);
    } catch {
      /* ignore */
    }
    syncMuteStateFromApi();
    if (typeof onUserReady === 'function') {
      onUserReady();
    }
    console.log('Jitsi: videoConferenceJoined (kullanıcı toplantıya katıldı)');
  }, [onUserReady, writeJoinLock, syncMuteStateFromApi]);

  const closePermissionPrompt = useCallback(() => {
    setPermissionPromptKind(null);
  }, []);

  const handlePermissionGranted = useCallback(() => {
    const kind = permissionPromptKind;
    closePermissionPrompt();
    if (!hasStartedMeeting) {
      if (kind === "audio") setAudioMuted(false);
      if (kind === "video") setVideoMuted(false);
      return;
    }
    const api = jitsiApiRef.current;
    if (!api?.executeCommand) return;
    if (kind === "audio") api.executeCommand("toggleAudio");
    if (kind === "video") api.executeCommand("toggleVideo");
  }, [closePermissionPrompt, permissionPromptKind, hasStartedMeeting]);

  const handlePermissionDenied = useCallback(
    ({ browserDenied } = {}) => {
      if (browserDenied) {
        toast.error(
          t("camera_microphone_permission_denied_reset", {
            defaultValue:
              "Kamera veya mikrofon erişimi sağlanamadı. Tarayıcı ayarlarından izinleri sıfırlayın.",
          })
        );
      }
      closePermissionPrompt();
    },
    [closePermissionPrompt, t]
  );

  const handlePermissionDialogClose = useCallback(() => {
    closePermissionPrompt();
  }, [closePermissionPrompt]);

  const handleToggleAudio = useCallback(() => {
    if (permissionPromptKind) return;

    if (!hasStartedMeeting) {
      if (!audioMuted) {
        setAudioMuted(true);
        return;
      }
      const consent = readMeetingPermissionConsent(roomName);
      if (consent.microphonePermissionConsent) {
        setAudioMuted(false);
        return;
      }
      setPermissionPromptKind("audio");
      return;
    }

    const api = jitsiApiRef.current;
    if (!api?.executeCommand) return;

    if (!audioMuted) {
      api.executeCommand("toggleAudio");
      return;
    }

    const consent = readMeetingPermissionConsent(roomName);
    if (consent.microphonePermissionConsent) {
      api.executeCommand("toggleAudio");
      return;
    }
    setPermissionPromptKind("audio");
  }, [hasStartedMeeting, audioMuted, permissionPromptKind, roomName]);

  const handleToggleVideo = useCallback(() => {
    if (permissionPromptKind) return;

    if (!hasStartedMeeting) {
      if (!videoMuted) {
        setVideoMuted(true);
        return;
      }
      const consent = readMeetingPermissionConsent(roomName);
      if (consent.cameraPermissionConsent) {
        setVideoMuted(false);
        return;
      }
      setPermissionPromptKind("video");
      return;
    }

    const api = jitsiApiRef.current;
    if (!api?.executeCommand) return;

    if (!videoMuted) {
      api.executeCommand("toggleVideo");
      return;
    }

    const consent = readMeetingPermissionConsent(roomName);
    if (consent.cameraPermissionConsent) {
      api.executeCommand("toggleVideo");
      return;
    }
    setPermissionPromptKind("video");
  }, [hasStartedMeeting, videoMuted, permissionPromptKind, roomName]);

  const handleJoin = useCallback(() => {
    leftNotifiedRef.current = false;
    hasJoinedRef.current = false;
    setJoinMediaPrefs({ audioMuted, videoMuted });
    setHasStartedMeeting(true);
  }, [audioMuted, videoMuted]);

  const handleLeave = useCallback(() => {
    try {
      if (jitsiApiRef.current?.executeCommand) {
        jitsiApiRef.current.executeCommand("hangup");
      } else {
        handleVideoConferenceLeft();
      }
    } catch {
      handleVideoConferenceLeft();
    }
  }, [handleVideoConferenceLeft]);

  useEffect(() => {
    if (!isConferenceJoined || !joinLockKey || typeof window === 'undefined') return;

    writeJoinLock();

    const beatId = window.setInterval(() => {
      const lock = readJoinLock();
      const isOwned = lock?.sessionId === lockSessionIdRef.current;
      const isExpired = !lock || (Date.now() - lock.updatedAt > JOIN_LOCK_TTL_MS);

      if (!isOwned && !isExpired) {
        handleJoinLockLost();
        return;
      }

      writeJoinLock();
    }, 2000);

    const onStorage = (event) => {
      if (event.key !== joinLockKey) return;
      const lock = readJoinLock();
      if (!lock) return;
      if (lock.sessionId !== lockSessionIdRef.current) {
        handleJoinLockLost();
      }
    };

    window.addEventListener('storage', onStorage);

    return () => {
      window.clearInterval(beatId);
      window.removeEventListener('storage', onStorage);
    };
  }, [isConferenceJoined, joinLockKey, handleJoinLockLost, writeJoinLock, readJoinLock]);

  const handleJitsiReady = useCallback((api) => {
    jitsiApiRef.current = api;
    setIsApiReady(true);

    // Sunucu varsayılanı prejoin açsa bile kapalı tut
    try {
      api.executeCommand?.('overwriteConfig', {
        prejoinConfig: { enabled: false, hideDisplayName: true },
        prejoinPageEnabled: false,
        disablePrejoinPage: true,
      });
    } catch {
      /* ignore */
    }

    api.addListener('videoConferenceJoined', handleVideoConferenceJoined);
    api.addListener('videoConferenceLeft', handleVideoconferenceLeft);

    api.addListener('audioMuteStatusChanged', ({ muted }) => {
      setAudioMuted(!!muted);
    });
    api.addListener('videoMuteStatusChanged', ({ muted }) => {
      setVideoMuted(!!muted);
    });
    api.addListener('micError', () => {
      toast.error(
        t("microphone_could_not_open", {
          defaultValue:
            "Mikrofon açılamadı. Görüntü alanındaki Jitsi mikrofon düğmesine tıklayıp tarayıcı iznini kabul edin.",
        })
      );
    });
  }, [handleVideoconferenceLeft, handleVideoConferenceJoined, t]);

  const userInfo = useMemo(
    () => ({ displayName: displayName || '', email: '' }),
    [displayName]
  );

  if (!jwtToken) {
    return <div>No JWT token</div>
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        bgcolor: appColors.bgMuted,
        borderRadius: { xs: 0, md: 2 },
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: hasStartedMeeting ? appColors.landingNearBlack : "transparent",
        }}
      >
        {!hasStartedMeeting ? (
          <MeetingLobbyPreview
            displayName={displayName}
            audioMuted={audioMuted}
            videoMuted={videoMuted}
          />
        ) : (
          <JitsiMeeting
            domain={JITSI_DOMAIN}
            configOverwrite={configOverwrite}
            interfaceConfigOverwrite={interfaceConfigOverwrite}
            lang='tr'
            roomName={roomName}
            userInfo={userInfo}
            jwt={jwtToken}
            onApiReady={handleJitsiReady}
            onError={(error) => console.log('Jitsi meeting error', error)}
            getIFrameRef={(parentNode) => {
              // react-sdk parent div verir; allow yalnızca asıl <iframe> üzerinde geçerli
              if (!parentNode) return;
              parentNode.style.width = '100%';
              parentNode.style.height = '100%';
              parentNode.style.minHeight = '0';

              const mediaAllow =
                'camera; microphone; autoplay; fullscreen; display-capture; clipboard-write; speaker-selection';

              const applyIframeMediaAllow = (iframe) => {
                if (!iframe) return;
                iframe.style.width = '100%';
                iframe.style.height = '100%';
                iframe.style.border = '0';
                iframe.setAttribute('allow', mediaAllow);
                iframe.allow = mediaAllow;
              };

              const existing = parentNode.querySelector?.('iframe');
              if (existing) {
                applyIframeMediaAllow(existing);
                return;
              }

              const observer = new MutationObserver(() => {
                const iframe = parentNode.querySelector?.('iframe');
                if (iframe) {
                  applyIframeMediaAllow(iframe);
                  observer.disconnect();
                }
              });
              observer.observe(parentNode, { childList: true, subtree: true });
              window.setTimeout(() => observer.disconnect(), 10000);
            }}
          />
        )}
      </Box>
      {!hasStartedMeeting && (
        <MeetingMediaControls
          inCall={false}
          audioMuted={audioMuted}
          videoMuted={videoMuted}
          disabled={!!permissionPromptKind}
          onToggleAudio={handleToggleAudio}
          onToggleVideo={handleToggleVideo}
          onJoin={handleJoin}
          onLeave={handleLeave}
        />
      )}
      <MeetingDevicePermissionDialog
        open={!!permissionPromptKind}
        kind={permissionPromptKind}
        appointmentId={roomName}
        onClose={handlePermissionDialogClose}
        onGranted={handlePermissionGranted}
        onDenied={handlePermissionDenied}
      />
    </Box>
  )
}

export default Jitsi
